import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { apiUrl } from "../../../config/config.js";

// Configuración de Toast para SweetAlert2
const Toast = Swal.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	timer: 3000,
	timerProgressBar: true,
	didOpen: (toast) => {
		toast.onmouseenter = Swal.stopTimer;
		toast.onmouseleave = Swal.resumeTimer;
	},
});

const BusinessSettingsEditor = () => {
	// Estado para almacenar todas las configuraciones del negocio
	const [settings, setSettings] = useState({
		id: null,
		businessId: null,
		hotelName: "",
		address: "",
		year: "",
		checkInTime: "",
		checkOutTime: "",
		earlyCheckInAllowed: false,
		lateCheckOutAllowed: false,
		petsAllowed: false,
		smokingAllowed: false,
		extraBedAllowed: false,
		babyCribAllowed: false,
		extras: {
			// Estructura inicial del JSON de 'extras'
			roomExtras: [],
			foodExtras: [],
			serviceExtras: [],
			transportExtras: [],
			activityExtras: [],
			familyExtras: [],
			businessExtras: [],
		},
		paymentPolicy: "",
		currency: "",
		language: "",
		phoneNumber: "",
		email: "",
		callHours: "",
		chatHours: "",
		responsePolicy: "",
	});

	// Estados para el manejo de la UI
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [saving, setSaving] = useState(false);

	// Estado derivado para el checkbox de chat 24/7
	const [isChat247, setIsChat247] = useState(false);

	// Estado para manejar el nuevo extra a añadir (temporalmente)
	const [newExtraName, setNewExtraName] = useState("");
	const [newExtraPrice, setNewExtraPrice] = useState("");
	const [newExtraCategory, setNewExtraCategory] = useState("roomExtras"); // Categoría por defecto

	// Función genérica para realizar llamadas POST con fetch
	const fetchData = async (endpoint, body = null) => {
		const options = {
			method: "POST", // Force POST for all queries
			headers: {
				"Content-Type": "application/json",
				// Aquí podrías añadir un token de autenticación si lo necesitas
				// 'Authorization': `Bearer ${yourAuthToken}`,
			},
			body: body ? JSON.stringify(body) : null,
		};

		const response = await fetch(`${apiUrl}${endpoint}`, options);

		const responseData = await response.json();

		if (!response.ok || !responseData.ok) {
			throw new Error(responseData.message || `Error en la solicitud: ${response.statusText}`);
		}
		return responseData.data; // Return only the 'data' part of the response
	};

	// Efecto para cargar las configuraciones al montar el componente
	useEffect(() => {
		const fetchSettings = async () => {
			try {
				setLoading(true);
				setError(null);

				const data = await fetchData("/getBusinessSettings", { businessId: 1 });

				setSettings({
					id: data.id,
					businessId: data.businessId,
					hotelName: data.hotelName,
					address: data.address,
					year: data.year,
					checkInTime: data.checkInTime,
					checkOutTime: data.checkOutTime,
					earlyCheckInAllowed: data.earlyCheckInAllowed,
					lateCheckOutAllowed: data.lateCheckOutAllowed,
					petsAllowed: data.petsAllowed,
					smokingAllowed: data.smokingAllowed,
					extraBedAllowed: data.extraBedAllowed,
					babyCribAllowed: data.babyCribAllowed,
					extras: data.extras, // Ya es un objeto gracias al backend
					paymentPolicy: data.paymentPolicy,
					currency: data.currency,
					language: data.language,
					phoneNumber: data.phoneNumber,
					email: data.email,
					callHours: data.callHours,
					chatHours: data.chatHours,
					responsePolicy: data.responsePolicy,
				});

				setIsChat247(data.chatHours === "24/7");
			} catch (err) {
				console.error("Error al cargar las configuraciones del negocio:", err);
				setError(err.message || "No pudimos cargar las configuraciones del negocio. Intenta de nuevo más tarde.");
				Toast.fire({
					icon: "error",
					title: err.message || "Error al cargar las configuraciones.",
				});
			} finally {
				setLoading(false);
			}
		};

		fetchSettings();
	}, []);

	// Maneja los cambios en los campos de texto e inputs simples (no extras)
	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setSettings((prevSettings) => ({
			...prevSettings,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	// Maneja el cambio del checkbox 24/7 para el chat
	const handleChat247Change = (e) => {
		const checked = e.target.checked;
		setIsChat247(checked);
		setSettings((prevSettings) => ({
			...prevSettings,
			chatHours: checked ? "24/7" : "",
		}));
	};

	// --- Funciones para la gestión de Extras ---

	// Maneja el cambio de un extra individual (nombre o precio)
	const handleExtraChange = (category, index, field, value) => {
		setSettings((prevSettings) => {
			const updatedExtras = { ...prevSettings.extras };
			const extra = { ...updatedExtras[category][index] };
			extra[field] = field === "price" ? parseFloat(value) || 0 : value; // Convertir precio a número
			updatedExtras[category][index] = extra;
			return {
				...prevSettings,
				extras: updatedExtras,
			};
		});
	};

	// Eliminar un extra
	const handleRemoveExtra = (category, index) => {
		Swal.fire({
			title: "¿Estás seguro?",
			text: "¡No podrás revertir esto!",
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: "Sí, eliminarlo!",
			cancelButtonText: "Cancelar",
		}).then((result) => {
			if (result.isConfirmed) {
				setSettings((prevSettings) => {
					const updatedExtras = { ...prevSettings.extras };
					updatedExtras[category] = updatedExtras[category].filter((_, i) => i !== index);
					return {
						...prevSettings,
						extras: updatedExtras,
					};
				});
				Toast.fire({ icon: "success", title: "Extra eliminado." });
			}
		});
	};

	// Añadir un nuevo extra
	const handleAddExtra = () => {
		if (!newExtraName.trim() || !newExtraPrice.trim() || isNaN(parseFloat(newExtraPrice))) {
			Toast.fire({ icon: "error", title: "Por favor, ingresa un nombre y un precio válido para el extra." });
			return;
		}

		setSettings((prevSettings) => {
			const updatedExtras = { ...prevSettings.extras };
			// Asegurarse de que la categoría exista y sea un array
			if (!updatedExtras[newExtraCategory] || !Array.isArray(updatedExtras[newExtraCategory])) {
				updatedExtras[newExtraCategory] = [];
			}
			updatedExtras[newExtraCategory].push({
				name: newExtraName.trim(),
				price: parseFloat(newExtraPrice),
			});
			return {
				...prevSettings,
				extras: updatedExtras,
			};
		});

		// Limpiar los campos del nuevo extra
		setNewExtraName("");
		setNewExtraPrice("");
		Toast.fire({ icon: "success", title: "Extra añadido." });
	};

	// Función para guardar todas las configuraciones
	const handleSave = async () => {
		try {
			setSaving(true);
			setError(null);

			// Prepara los datos a enviar:
			// Booleanos a 1/0, 'extras' ya es un objeto en el estado, backend lo stringificará.
			const dataToSave = {
				...settings,
				earlyCheckInAllowed: settings.earlyCheckInAllowed ? 1 : 0,
				lateCheckOutAllowed: settings.lateCheckOutAllowed ? 1 : 0,
				petsAllowed: settings.petsAllowed ? 1 : 0,
				smokingAllowed: settings.smokingAllowed ? 1 : 0,
				extraBedAllowed: settings.extraBedAllowed ? 1 : 0,
				babyCribAllowed: settings.babyCribAllowed ? 1 : 0,
				extras: settings.extras, // Ya es un objeto, listo para que el backend lo stringifique
				chatHours: isChat247 ? "24/7" : settings.chatHours,
			};

			delete dataToSave.id;

			const url = "/updateBusinessSettings";
			await fetchData(url, dataToSave);

			Toast.fire({
				icon: "success",
				title: "Configuraciones guardadas exitosamente.",
			});
		} catch (err) {
			console.error("Error al guardar las configuraciones del negocio:", err);
			setError(err.message || "Hubo un error al guardar las configuraciones. Por favor, intenta de nuevo.");
			Toast.fire({
				icon: "error",
				title: err.message || "Error al guardar las configuraciones.",
			});
		} finally {
			setSaving(false);
		}
	};

	// Función para descartar cambios y recargar los datos originales
	const handleDiscard = async () => {
		setLoading(true);
		try {
			const data = await fetchData("/getBusinessSettings", { businessId: 1 });

			setSettings({
				id: data.id,
				businessId: data.businessId,
				hotelName: data.hotelName,
				address: data.address,
				year: data.year,
				checkInTime: data.checkInTime,
				checkOutTime: data.checkOutTime,
				earlyCheckInAllowed: data.earlyCheckInAllowed,
				lateCheckOutAllowed: data.lateCheckOutAllowed,
				petsAllowed: data.petsAllowed,
				smokingAllowed: data.smokingAllowed,
				extraBedAllowed: data.extraBedAllowed,
				babyCribAllowed: data.babyCribAllowed,
				extras: data.extras,
				paymentPolicy: data.paymentPolicy,
				currency: data.currency,
				language: data.language,
				phoneNumber: data.phoneNumber,
				email: data.email,
				callHours: data.callHours,
				chatHours: data.chatHours,
				responsePolicy: data.responsePolicy,
			});
			setIsChat247(data.chatHours === "24/7");
			Toast.fire({
				icon: "info",
				title: "Cambios descartados.",
			});
		} catch (err) {
			console.error("Error al descartar cambios y recargar:", err);
			Toast.fire({
				icon: "error",
				title: err.message || "Error al descartar cambios.",
			});
		} finally {
			setLoading(false);
		}
	};

	// Obtener las categorías de extras para el selector
	const extraCategories = Object.keys(settings.extras);

	if (loading) {
		return (
			<div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Cargando...</span>
				</div>
				<p className="ms-2">Cargando configuraciones del negocio...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="alert alert-danger" role="alert">
				{error}
			</div>
		);
	}

	return (
		<div className="container-fluid mt-3">
			<h2 className="mb-4">⚙️ Configuración General del Negocio</h2>
			<p className="text-muted">Gestiona todas las configuraciones principales de tu negocio aquí.</p>
			<hr />
			{/* Sección de Contacto e Información General */}
			<div className="card mb-4">
				<div className="card-header">
					<h5 className="mb-0">Información de Contacto y General</h5>
				</div>
				<div className="card-body">
					<div className="row">
						<div className="col-md-8">
							<div className="mb-3">
								<label htmlFor="hotelName" className="form-label">
									Nombre del Hotel
								</label>
								<input
									type="text"
									id="hotelName"
									name="hotelName"
									value={settings.hotelName || ""}
									onChange={handleChange}
									className="form-control"
								/>
							</div>
						</div>
						<div className="col-md-4">
							<div className="mb-3">
								<label htmlFor="year" className="form-label">
									Año
								</label>
								<input
									type="number"
									id="year"
									name="year"
									value={settings.year || ""}
									onChange={handleChange}
									className="form-control"
								/>
							</div>
						</div>
					</div>
					<div className="mb-3">
						<label htmlFor="address" className="form-label">
							Dirección
						</label>
						<textarea
							id="address"
							name="address"
							rows="3"
							value={settings.address || ""}
							onChange={handleChange}
							className="form-control"></textarea>
					</div>
					<div className="row">
						<div className="col-md-6">
							<div className="mb-3">
								<label htmlFor="phoneNumber" className="form-label">
									Número de Teléfono
								</label>
								<input
									type="tel"
									id="phoneNumber"
									name="phoneNumber"
									value={settings.phoneNumber}
									onChange={handleChange}
									placeholder="+506 8888-8888"
									className="form-control"
								/>
							</div>
						</div>
						<div className="col-md-6">
							<div className="mb-3">
								<label htmlFor="email" className="form-label">
									Correo Electrónico
								</label>
								<input
									type="email"
									id="email"
									name="email"
									value={settings.email}
									onChange={handleChange}
									placeholder="reservas@tuhotel.com"
									className="form-control"
								/>
							</div>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<div className="mb-3">
								<label htmlFor="currency" className="form-label">
									Moneda
								</label>
								<input
									type="text"
									id="currency"
									name="currency"
									value={settings.currency}
									onChange={handleChange}
									placeholder="Ej: USD, CRC"
									className="form-control"
								/>
							</div>
						</div>
						<div className="col-md-6">
							<div className="mb-3">
								<label htmlFor="language" className="form-label">
									Idioma
								</label>
								<input
									type="text"
									id="language"
									name="language"
									value={settings.language}
									onChange={handleChange}
									placeholder="Ej: es, en"
									className="form-control"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			---
			{/* Sección de Horarios y Políticas */}
			<div className="card mb-4">
				<div className="card-header">
					<h5 className="mb-0">Horarios y Políticas Generales</h5>
				</div>
				<div className="card-body">
					<div className="row">
						<div className="col-md-6">
							<div className="mb-3">
								<label htmlFor="checkInTime" className="form-label">
									Hora de Check-in
								</label>
								<input
									type="time"
									id="checkInTime"
									name="checkInTime"
									value={settings.checkInTime}
									onChange={handleChange}
									className="form-control"
								/>
							</div>
						</div>
						<div className="col-md-6">
							<div className="mb-3">
								<label htmlFor="checkOutTime" className="form-label">
									Hora de Check-out
								</label>
								<input
									type="time"
									id="checkOutTime"
									name="checkOutTime"
									value={settings.checkOutTime}
									onChange={handleChange}
									className="form-control"
								/>
							</div>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6">
							<div className="mb-3">
								<label htmlFor="callHours" className="form-label">
									Horario de Llamadas
								</label>
								<input
									type="text"
									id="callHours"
									name="callHours"
									value={settings.callHours}
									onChange={handleChange}
									placeholder="Ej: 08:00 - 20:00"
									className="form-control"
								/>
							</div>
						</div>
						<div className="col-md-6">
							<div className="mb-3">
								<label htmlFor="paymentPolicy" className="form-label">
									Política de Pagos
								</label>
								<input
									type="text"
									id="paymentPolicy"
									name="paymentPolicy"
									value={settings.paymentPolicy}
									onChange={handleChange}
									placeholder="Ej: 50% al reservar, 50% al check-in"
									className="form-control"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			---
			{/* Sección de Permisos */}
			<div className="card mb-4">
				<div className="card-header">
					<h5 className="mb-0">Permisos y Restricciones</h5>
				</div>
				<div className="card-body">
					<div className="row">
						<div className="col-md-6">
							<div className="mb-3 form-check">
								<input
									type="checkbox"
									id="earlyCheckInAllowed"
									name="earlyCheckInAllowed"
									checked={settings.earlyCheckInAllowed}
									onChange={handleChange}
									className="form-check-input"
								/>
								<label className="form-check-label" htmlFor="earlyCheckInAllowed">
									Check-in Temprano Permitido
								</label>
							</div>
							<div className="mb-3 form-check">
								<input
									type="checkbox"
									id="lateCheckOutAllowed"
									name="lateCheckOutAllowed"
									checked={settings.lateCheckOutAllowed}
									onChange={handleChange}
									className="form-check-input"
								/>
								<label className="form-check-label" htmlFor="lateCheckOutAllowed">
									Check-out Tardío Permitido
								</label>
							</div>
							<div className="mb-3 form-check">
								<input
									type="checkbox"
									id="petsAllowed"
									name="petsAllowed"
									checked={settings.petsAllowed}
									onChange={handleChange}
									className="form-check-input"
								/>
								<label className="form-check-label" htmlFor="petsAllowed">
									Mascotas Permitidas
								</label>
							</div>
						</div>
						<div className="col-md-6">
							<div className="mb-3 form-check">
								<input
									type="checkbox"
									id="smokingAllowed"
									name="smokingAllowed"
									checked={settings.smokingAllowed}
									onChange={handleChange}
									className="form-check-input"
								/>
								<label className="form-check-label" htmlFor="smokingAllowed">
									Fumar Permitido
								</label>
							</div>
							<div className="mb-3 form-check">
								<input
									type="checkbox"
									id="extraBedAllowed"
									name="extraBedAllowed"
									checked={settings.extraBedAllowed}
									onChange={handleChange}
									className="form-check-input"
								/>
								<label className="form-check-label" htmlFor="extraBedAllowed">
									Cama Adicional Permitida
								</label>
							</div>
							<div className="mb-3 form-check">
								<input
									type="checkbox"
									id="babyCribAllowed"
									name="babyCribAllowed"
									checked={settings.babyCribAllowed}
									onChange={handleChange}
									className="form-check-input"
								/>
								<label className="form-check-label" htmlFor="babyCribAllowed">
									Cuna para Bebé Permitida
								</label>
							</div>
						</div>
					</div>
				</div>
			</div>
			---
			{/* Sección de Extras Dinámicos */}
			<div className="card mb-4">
				<div className="card-header">
					<h5 className="mb-0">📦 Extras Disponibles</h5>
				</div>
				<div className="card-body">
					{Object.entries(settings.extras).map(([category, items]) => (
						<div key={category} className="mb-4">
							<h6>{category.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}</h6> {/* Formato legible */}
							<hr className="mt-1 mb-2" />
							{items.length > 0 ? (
								<ul className="list-group">
									{items.map((item, index) => (
										<li key={index} className="list-group-item d-flex align-items-center mb-2">
											<div className="row flex-grow-1 g-2">
												<div className="col-md-6">
													<input
														type="text"
														className="form-control"
														placeholder="Nombre del extra"
														value={item.name}
														onChange={(e) => handleExtraChange(category, index, "name", e.target.value)}
													/>
												</div>
												<div className="col-md-4">
													<div className="input-group">
														<span className="input-group-text">$</span>
														<input
															type="number"
															step="0.01"
															className="form-control"
															placeholder="Precio"
															value={item.price}
															onChange={(e) => handleExtraChange(category, index, "price", e.target.value)}
														/>
													</div>
												</div>
											</div>
											<button
												type="button"
												className="btn btn-outline-danger btn-sm ms-2"
												onClick={() => handleRemoveExtra(category, index)}>
												Eliminar
											</button>
										</li>
									))}
								</ul>
							) : (
								<p className="text-muted">No hay extras en esta categoría.</p>
							)}
						</div>
					))}

					<h6 className="mt-4">Agregar Nuevo Extra</h6>
					<hr className="mt-1 mb-2" />
					<div className="row g-2">
						<div className="col-md-4">
							<select className="form-select" value={newExtraCategory} onChange={(e) => setNewExtraCategory(e.target.value)}>
								{extraCategories.map((cat) => (
									<option key={cat} value={cat}>
										{cat.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
									</option>
								))}
							</select>
						</div>
						<div className="col-md-4">
							<input
								type="text"
								className="form-control"
								placeholder="Nombre del extra"
								value={newExtraName}
								onChange={(e) => setNewExtraName(e.target.value)}
							/>
						</div>
						<div className="col-md-2">
							<div className="input-group">
								<span className="input-group-text">$</span>
								<input
									type="number"
									step="0.01"
									className="form-control"
									placeholder="Precio"
									value={newExtraPrice}
									onChange={(e) => setNewExtraPrice(e.target.value)}
								/>
							</div>
						</div>
						<div className="col-md-2">
							<button type="button" className="btn btn-outline-primary w-100" onClick={handleAddExtra}>
								Añadir Extra
							</button>
						</div>
					</div>
				</div>
			</div>
			---
			{/* Sección de Configuraciones de Chat de IA */}
			<div className="card mb-4">
				<div className="card-header">
					<h5 className="mb-0">🤖 Configuraciones del Chat de IA</h5>
				</div>
				<div className="card-body">
					<div className="mb-3 form-check">
						<input
							type="checkbox"
							id="is247Chat"
							name="is247"
							checked={isChat247}
							onChange={handleChat247Change}
							className="form-check-input"
						/>
						<label className="form-check-label" htmlFor="is247Chat">
							Habilitar Chat 24/7
						</label>
					</div>
					{!isChat247 && (
						<div className="mb-3">
							<label htmlFor="chatHours" className="form-label">
								Horario de Chat (ej. L-V 09:00 - 18:00)
							</label>
							<input
								type="text"
								id="chatHours"
								name="chatHours"
								value={settings.chatHours}
								onChange={handleChange}
								placeholder="Ej: L-V 08:00 - 20:00"
								className="form-control"
							/>
							<div className="form-text text-muted">Ingresa el horario en el que tu asistente de IA estará disponible.</div>
						</div>
					)}
					<div className="mb-3">
						<label htmlFor="responsePolicy" className="form-label">
							Mensaje de Política de Respuesta del Chat
						</label>
						<textarea
							id="responsePolicy"
							name="responsePolicy"
							rows="3"
							value={settings.responsePolicy}
							onChange={handleChange}
							placeholder="Este mensaje se mostrará a los usuarios para informarles sobre el tiempo de respuesta esperado."
							className="form-control"></textarea>
						<div className="form-text text-muted">Describe el tiempo de respuesta esperado para los mensajes del chat.</div>
					</div>
				</div>
			</div>
			---
			{/* Botones de Acción */}
			<div className="mb-3 d-flex justify-content-around ">
				<button type="button" onClick={handleSave} className="btn btn-outline-theme btn-lg" disabled={saving}>
					{saving ? "Guardando..." : "Actualizar"}
				</button>
				<button type="button" onClick={handleDiscard} className="btn btn-outline-theme btn-lg" disabled={saving}>
					Descartar Cambios
				</button>
			</div>
		</div>
	);
};

export default BusinessSettingsEditor;
